import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { Icon, type IconName } from '../components/Icon.tsx';
import { Shell } from '../components/Shell.tsx';
import { Button, Dialog, EmptyState, Field, IconButton, Skeleton, StatusBadge, Switch, type Tone } from '../components/ui.tsx';
import { locale, t, tn } from '../i18n/index.ts';
import { action } from '../lib/api.ts';
import { day, money, parseMoney, todayIso } from '../lib/format.ts';
import { useSession } from '../lib/session.tsx';
import { qtyText } from './Invoices.tsx';
import { useAction, useSave } from './manage/common.tsx';
import { Kpi, Table } from './Reports.tsx';

type VisitState = 'planned' | 'done' | 'skipped';
type Outcome = 'order' | 'no_order' | 'closed';
interface DayVisit {
  id: string;
  sequence: number;
  state: VisitState;
  outcome: Outcome | null;
  visited_at: string | null;
  note: string | null;
  collected: number;
  customer: string;
  customer_name: string;
  phone: string | null;
  address: string | null;
  route_name: string | null;
  order: { id: string; name: string; total: number } | null;
  open: number;
  overdue: number;
  credit_limit: number | null;
  over_limit: boolean;
}
interface DayData {
  date: string;
  today: string;
  rep: { id: string; name: string };
  visits: DayVisit[];
  totals: { planned: number; done: number; skipped: number; orders: number; ordered: number; collected: number };
  cash: { held: number; count: number; since: string | null };
}
interface VisitDetail {
  visit: { id: string; date: string; state: VisitState; outcome: Outcome | null; order: string | null; collected: number; note: string | null };
  customer: { id: string; name: string; phone: string | null; address: string | null; credit_limit: number | null; payment_days: number };
  balance: { open: number; overdue: number };
  open: { id: string; name: string; date: string; due_date: string; owed: number; overdue: boolean }[];
  orders: { id: string; name: string; date: string; state: string; total: number }[];
  collections: { id: string; amount: number; created_at: string }[];
  today: string;
}
interface RouteRow {
  id: string;
  name: string;
  rep: string;
  rep_name: string;
  days: number[];
  active: boolean;
  note: string | null;
  stops: number;
}
interface Person {
  id: string;
  name: string;
}
interface Sellable {
  id: string;
  name: string;
  sku: string | null;
  tax_rate: number;
  price: number;
  special: boolean;
  on_hand: number | null;
}

type Tab = 'day' | 'reps' | 'routes' | 'cash';
const TABS: { key: Tab; icon: IconName; permission: string[] }[] = [
  { key: 'day', icon: 'truck', permission: ['distribution.field'] },
  { key: 'reps', icon: 'users', permission: ['distribution.routes'] },
  { key: 'routes', icon: 'transfer', permission: ['distribution.field'] },
  { key: 'cash', icon: 'cash', permission: ['distribution.cash'] },
];
const STATE: Record<VisitState, { tone: Tone; icon: IconName }> = {
  planned: { tone: 'neutral', icon: 'clock' },
  done: { tone: 'ok', icon: 'checkCircle' },
  skipped: { tone: 'warn', icon: 'minus' },
};

/** Short weekday names, Monday first. */
const weekdayName = (d: number) => new Intl.DateTimeFormat(locale(), { weekday: 'short', timeZone: 'UTC' }).format(new Date(Date.UTC(2024, 0, d)));

export function Distribution() {
  const { can } = useSession();
  const [params, setParams] = useSearchParams();
  const tabs = TABS.filter((x) => x.permission.some(can));
  const asked = params.get('tab');
  const tab = (tabs.find((x) => x.key === asked) ?? tabs[0])?.key ?? 'day';
  return (
    <Shell>
      <div className="page reports distribution">
        <div className="page-head">
          <div>
            <h1>{t('dst.title')}</h1>
            <p className="muted">{t('dst.subtitle')}</p>
          </div>
        </div>
        {tabs.length > 1 ? (
          <div className="segmented tabs" role="tablist" aria-label={t('dst.title')}>
            {tabs.map((x) => (
              <button key={x.key} role="tab" aria-selected={tab === x.key} className={tab === x.key ? 'active' : ''} onClick={() => setParams(x.key === tabs[0]!.key ? {} : { tab: x.key }, { replace: true })}>
                <Icon name={x.icon} size={16} />
                {t(`dst.tab_${x.key}`)}
              </button>
            ))}
          </div>
        ) : null}
        {tab === 'reps' ? <RepsTab /> : tab === 'routes' ? <RoutesTab /> : tab === 'cash' ? <CashTab /> : <DayTab />}
      </div>
    </Shell>
  );
}

/* ---------------------------------------------------------------- The rep's day */

function DayTab() {
  const { can, me } = useSession();
  const [params] = useSearchParams();
  const [date, setDate] = useState(todayIso());
  const [rep, setRep] = useState<string>(params.get('rep') ?? me?.userId ?? '');
  const planner = can('distribution.routes');
  const { data: reps } = useAction<Person[]>('distribution.reps', {});
  const { data, reload } = useAction<DayData>('distribution.day', { date, rep: rep || undefined });
  const [open, setOpen] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const d = data && data.date === date && (!rep || data.rep.id === rep) ? data : null;
  const isToday = d ? d.date === d.today : false;
  return (
    <div className="rep-grid">
      <div className="toolbar dst-bar">
        <label className="vat-month">
          <span>{t('dst.date')}</span>
          <input className="input date-input" type="date" value={date} onChange={(e) => e.target.value && setDate(e.target.value)} />
        </label>
        {planner && reps ? (
          <label className="vat-month">
            <span>{t('dst.rep')}</span>
            <select className="input" value={rep} onChange={(e) => setRep(e.target.value)}>
              {reps.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </label>
        ) : null}
        {d && d.date >= d.today ? (
          <Button icon="plus" className="dst-push" onClick={() => setAdding(true)}>
            {t('dst.add_shop')}
          </Button>
        ) : null}
      </div>
      {!d ? (
        <>
          <Skeleton h={112} r="var(--radius-lg)" />
          <Skeleton h={300} r="var(--radius-lg)" />
        </>
      ) : (
        <>
          <section className="rep-kpis" aria-label={t('dst.totals')}>
            <Kpi label={t('dst.k_visits')} value={`${d.totals.done}/${d.totals.planned}`} hint={d.totals.skipped ? tn('dst.skipped_n', d.totals.skipped) : undefined} />
            <Kpi label={t('dst.k_orders')} value={money(d.totals.ordered)} hint={tn('dst.orders_n', d.totals.orders)} />
            <Kpi label={t('dst.k_collected')} value={money(d.totals.collected)} />
            <Kpi label={t('dst.k_cash')} value={money(d.cash.held)} hint={d.cash.since ? t('dst.cash_since', { date: day(d.cash.since) }) : t('dst.cash_none')} warn={d.cash.held > 0 && !!d.cash.since && d.cash.since < d.today} />
          </section>
          {!d.visits.length ? (
            <div className="rep-wide">
              <EmptyState icon="truck" title={t('dst.no_visits')} body={t('dst.no_visits_body')} />
            </div>
          ) : (
            <ol className="dst-visits rep-wide" aria-label={t('dst.visits')}>
              {d.visits.map((v) => (
                <li key={v.id}>
                  <button type="button" className={`dst-visit dst-${v.state}`} onClick={() => setOpen(v.id)}>
                    <span className="dst-seq tabular" aria-hidden="true">
                      {v.state === 'done' ? <Icon name="check" size={16} /> : v.sequence}
                    </span>
                    <span className="dst-main">
                      <span className="dst-name">{v.customer_name}</span>
                      <span className="muted small">{[v.address, v.route_name].filter(Boolean).join(' · ') || t('dst.off_route')}</span>
                      <span className="dst-tags">
                        {v.overdue > 0 ? <span className="badge badge-danger">{t('dst.overdue', { amount: money(v.overdue) })}</span> : null}
                        {v.over_limit ? <span className="badge badge-warn">{t('dst.over_limit')}</span> : null}
                        {v.order ? <span className="badge badge-ok">{t('dst.ordered', { amount: money(v.order.total) })}</span> : null}
                        {v.collected ? <span className="badge badge-info">{t('dst.collected', { amount: money(v.collected) })}</span> : null}
                        {v.outcome && v.outcome !== 'order' ? <span className="badge">{t(`dst.o_${v.outcome}`)}</span> : null}
                      </span>
                    </span>
                    <StatusBadge tone={STATE[v.state].tone} icon={STATE[v.state].icon} label={t(`dst.st_${v.state}`)} />
                  </button>
                </li>
              ))}
            </ol>
          )}
          {!isToday && d.visits.length ? <p className="muted small rep-wide">{d.date < d.today ? t('dst.past_hint') : t('dst.ahead_hint')}</p> : null}
        </>
      )}
      {open && d ? (
        <VisitDialog
          id={open}
          editable={isToday}
          onClose={() => setOpen(null)}
          onChanged={() => void reload()}
        />
      ) : null}
      {adding && d ? (
        <AddShop
          date={d.date}
          rep={d.rep.id}
          onClose={() => setAdding(false)}
          onDone={() => {
            setAdding(false);
            void reload();
          }}
        />
      ) : null}
    </div>
  );
}

function AddShop({ date, rep, onClose, onDone }: { date: string; rep: string; onClose: () => void; onDone: () => void }) {
  const { data } = useAction<{ id: string; name: string; town: string | null; active: boolean }[]>('invoicing.customers', {});
  const [search, setSearch] = useState('');
  const { busy, run } = useSave();
  const found = (data ?? []).filter((c) => c.active !== false && c.name.toLowerCase().includes(search.trim().toLowerCase())).slice(0, 50);
  return (
    <Dialog title={t('dst.add_shop')} onClose={onClose} size="sm">
      <div className="form-stack">
        <Field label={t('dst.find_customer')}>
          <input className="input" type="search" value={search} onChange={(e) => setSearch(e.target.value)} data-autofocus />
        </Field>
        {!data ? (
          <Skeleton h={160} />
        ) : (
          <ul className="dst-pick">
            {found.map((c) => (
              <li key={c.id}>
                <button type="button" disabled={busy} onClick={async () => (await run(() => action('distribution.add_visit', { date, rep, customer: c.id }), t('dst.added'))) && onDone()}>
                  <span>{c.name}</span>
                  {c.town ? <span className="muted small">{c.town}</span> : null}
                </button>
              </li>
            ))}
            {!found.length ? <li className="muted">{t('dst.no_match')}</li> : null}
          </ul>
        )}
      </div>
    </Dialog>
  );
}

/* ---------------------------------------------------------------- A visit */

function VisitDialog({ id, editable, onClose, onChanged }: { id: string; editable: boolean; onClose: () => void; onChanged: () => void }) {
  const { data, reload } = useAction<VisitDetail>('distribution.visit', { visit: id });
  const [step, setStep] = useState<null | 'order' | 'collect' | 'no_order' | 'closed' | 'skip'>(null);
  const changed = () => {
    setStep(null);
    void reload();
    onChanged();
  };
  if (!data) {
    return (
      <Dialog title={t('common.loading')} onClose={onClose}>
        <Skeleton h={240} />
      </Dialog>
    );
  }
  const v = data.visit;
  const c = data.customer;
  const canWork = editable && v.state !== 'skipped';
  const limitLeft = c.credit_limit === null ? null : c.credit_limit - data.balance.open;
  return (
    <Dialog
      title={c.name}
      onClose={onClose}
      size="lg"
      footer={
        canWork ? (
          <>
            <span className="foot-actions">
              {v.state === 'planned' ? (
                <Button variant="ghost" icon="minus" onClick={() => setStep('skip')}>
                  {t('dst.skip')}
                </Button>
              ) : null}
              {!v.order && !v.outcome ? (
                <>
                  <Button variant="ghost" onClick={() => setStep('no_order')}>
                    {t('dst.o_no_order')}
                  </Button>
                  <Button variant="ghost" onClick={() => setStep('closed')}>
                    {t('dst.o_closed')}
                  </Button>
                </>
              ) : null}
            </span>
            <span className="foot-actions">
              {data.balance.open > 0 ? (
                <Button icon="cash" onClick={() => setStep('collect')}>
                  {t('dst.collect')}
                </Button>
              ) : null}
              {!v.order ? (
                <Button variant="primary" icon="bag" onClick={() => setStep('order')}>
                  {t('dst.take_order')}
                </Button>
              ) : null}
            </span>
          </>
        ) : undefined
      }
    >
      <div className="form-stack dst-detail">
        <div className="pay-head">
          <StatusBadge tone={STATE[v.state].tone} icon={STATE[v.state].icon} label={t(`dst.st_${v.state}`)} />
          {v.outcome && v.outcome !== 'order' ? <span className="badge">{t(`dst.o_${v.outcome}`)}</span> : null}
          {c.address ? <span className="muted">{c.address}</span> : null}
          {c.phone ? (
            <a className="btn btn-ghost dst-call" href={`tel:${c.phone}`}>
              <Icon name="phone" size={16} />
              {c.phone}
            </a>
          ) : null}
        </div>
        {v.note ? <p className="muted">{v.note}</p> : null}
        <section className="rep-kpis dst-kpis" aria-label={t('dst.balance')}>
          <Kpi label={t('dst.k_open')} value={money(data.balance.open)} />
          <Kpi label={t('dst.k_overdue')} value={money(data.balance.overdue)} warn={data.balance.overdue > 0} hint={data.balance.overdue > 0 ? t('dst.collect_first') : undefined} />
          <Kpi
            label={t('dst.k_limit')}
            value={limitLeft === null ? t('dst.no_limit') : money(Math.max(0, limitLeft))}
            hint={c.credit_limit === null ? undefined : t('dst.limit_of', { limit: money(c.credit_limit) })}
            warn={limitLeft !== null && limitLeft <= 0}
          />
        </section>
        {v.order ? (
          <p className="acc-check ok" role="status">
            <Icon name="checkCircle" size={16} />
            {t('dst.order_taken', { name: data.orders.find((o) => o.id === v.order)?.name ?? '', amount: money(data.orders.find((o) => o.id === v.order)?.total ?? 0) })}
          </p>
        ) : null}
        <section className="panel" aria-labelledby="dst-open">
          <div className="panel-head">
            <h2 id="dst-open">{t('dst.open_invoices')}</h2>
          </div>
          <Table
            head={[t('dst.c_invoice'), t('dst.c_due'), t('dst.c_owed')]}
            empty={t('dst.nothing_owed')}
            rows={data.open.map((i) => ({
              key: i.id,
              cells: [
                <span className="tabular">{i.name}</span>,
                <span className={i.overdue ? 'text-danger tabular' : 'tabular'}>{day(i.due_date)}</span>,
                <span className="tabular strong">{money(i.owed)}</span>,
              ],
            }))}
          />
        </section>
        {data.orders.length ? (
          <section className="panel" aria-labelledby="dst-orders">
            <div className="panel-head">
              <h2 id="dst-orders">{t('dst.last_orders')}</h2>
            </div>
            <Table
              head={[t('dst.c_order'), t('dst.c_date'), t('dst.c_total')]}
              rows={data.orders.map((o) => ({ key: o.id, cells: [<span className="tabular">{o.name}</span>, <span className="tabular">{day(o.date)}</span>, <span className="tabular">{money(o.total)}</span>] }))}
            />
          </section>
        ) : null}
        {!editable ? <p className="muted small">{t('dst.not_today')}</p> : null}
      </div>
      {step === 'order' ? <OrderDialog visit={v.id} customer={c} limitLeft={limitLeft} onClose={() => setStep(null)} onDone={changed} /> : null}
      {step === 'collect' ? <CollectDialog data={data} onClose={() => setStep(null)} onDone={changed} /> : null}
      {step === 'no_order' || step === 'closed' || step === 'skip' ? <FinishDialog visit={v.id} kind={step} onClose={() => setStep(null)} onDone={changed} /> : null}
    </Dialog>
  );
}

function FinishDialog({ visit, kind, onClose, onDone }: { visit: string; kind: 'no_order' | 'closed' | 'skip'; onClose: () => void; onDone: () => void }) {
  const [note, setNote] = useState('');
  const { busy, run } = useSave();
  const skip = kind === 'skip';
  const ok = !skip || note.trim().length >= 3;
  const save = async () => {
    const done = skip
      ? await run(() => action('distribution.skip_visit', { visit, note: note.trim() }), t('dst.skipped'))
      : await run(() => action('distribution.finish_visit', { visit, outcome: kind, note: note.trim() || null }), t('dst.saved'));
    if (done) onDone();
  };
  return (
    <Dialog
      title={skip ? t('dst.skip_title') : t(`dst.o_${kind}`)}
      onClose={onClose}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" icon="check" busy={busy} disabled={!ok} onClick={save}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="form-stack">
        <Field label={skip ? t('dst.skip_why') : t('dst.note')} hint={skip ? undefined : t('dst.note_hint')} required={skip}>
          <textarea className="input" rows={3} maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} data-autofocus />
        </Field>
      </div>
    </Dialog>
  );
}

function OrderDialog({
  visit,
  customer,
  limitLeft,
  onClose,
  onDone,
}: {
  visit: string;
  customer: VisitDetail['customer'];
  limitLeft: number | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const { data } = useAction<Sellable[]>('invoicing.products', { customer: customer.id });
  const [qty, setQty] = useState<Record<string, number>>({});
  const [search, setSearch] = useState('');
  const [delivery, setDelivery] = useState(() => {
    const d = new Date(`${todayIso()}T00:00:00Z`);
    d.setUTCDate(d.getUTCDate() + 1);
    return d.toISOString().slice(0, 10);
  });
  const [note, setNote] = useState('');
  const { busy, run } = useSave();
  const products = data ?? [];
  const shown = useMemo(() => {
    const s = search.trim().toLowerCase();
    return products.filter((p) => !s || p.name.toLowerCase().includes(s) || (p.sku ?? '').toLowerCase().includes(s)).slice(0, 80);
  }, [products, search]);
  const chosen = products.filter((p) => (qty[p.id] ?? 0) > 0);
  const total = chosen.reduce((s, p) => {
    const net = Math.round(p.price * qty[p.id]!);
    return s + net + Math.round((net * p.tax_rate) / 100);
  }, 0);
  const set = (id: string, n: number) => setQty((q) => ({ ...q, [id]: Math.max(0, Math.min(100_000, n)) }));
  const save = async () => {
    const lines = chosen.map((p) => ({ product: p.id, name: p.name, qty: qty[p.id]!, unit_price: p.price, tax_rate: p.tax_rate }));
    if (await run(() => action('distribution.order', { visit, lines, delivery_date: delivery, note: note.trim() || null }), t('dst.order_saved'))) onDone();
  };
  return (
    <Dialog
      title={t('dst.order_for', { name: customer.name })}
      onClose={onClose}
      size="lg"
      footer={
        <>
          <span className="dst-total">
            <span className="muted small">{tn('dst.lines_n', chosen.length)}</span>
            <strong className="tabular">{money(total)}</strong>
          </span>
          <Button variant="primary" icon="check" busy={busy} disabled={!chosen.length} onClick={save}>
            {t('dst.save_order')}
          </Button>
        </>
      }
    >
      <div className="form-stack">
        {limitLeft !== null && total > limitLeft ? (
          <div className="callout callout-warn" role="status">
            <Icon name="alert" size={20} />
            <p>{t('dst.over_limit_body', { left: money(Math.max(0, limitLeft)) })}</p>
          </div>
        ) : null}
        <Field label={t('dst.find_product')}>
          <input className="input" type="search" value={search} onChange={(e) => setSearch(e.target.value)} data-autofocus />
        </Field>
        {!data ? (
          <Skeleton h={240} />
        ) : (
          <ul className="dst-products">
            {shown.map((p) => {
              const n = qty[p.id] ?? 0;
              return (
                <li key={p.id} className={n ? 'on' : undefined}>
                  <span className="dst-main">
                    <span className="dst-name">{p.name}</span>
                    <span className="muted small tabular">
                      {[t('dst.price_excl', { price: money(p.price), rate: p.tax_rate }), p.special ? t('dst.special') : null, p.on_hand !== null ? t('inv.in_stock', { qty: qtyText(p.on_hand) }) : null]
                        .filter(Boolean)
                        .join(' · ')}
                    </span>
                  </span>
                  <span className="dst-step">
                    <IconButton icon="minus" label={t('dst.less', { name: p.name })} onClick={() => set(p.id, n - 1)} disabled={!n} />
                    <input
                      className="input tabular"
                      inputMode="decimal"
                      aria-label={t('dst.qty_of', { name: p.name })}
                      value={n ? qtyText(n) : ''}
                      placeholder="0"
                      onChange={(e) => {
                        const x = Number(e.target.value.replace(',', '.'));
                        if (Number.isFinite(x)) set(p.id, Math.round(x * 1000) / 1000);
                      }}
                    />
                    <IconButton icon="plus" label={t('dst.more', { name: p.name })} onClick={() => set(p.id, n + 1)} />
                  </span>
                </li>
              );
            })}
            {!shown.length ? <li className="muted">{t('dst.no_match')}</li> : null}
          </ul>
        )}
        <div className="dn-fields">
          <Field label={t('dst.delivery_date')}>
            <input className="input" type="date" value={delivery} min={todayIso()} onChange={(e) => setDelivery(e.target.value)} />
          </Field>
          <Field label={t('dst.note')}>
            <input className="input" value={note} maxLength={500} onChange={(e) => setNote(e.target.value)} />
          </Field>
        </div>
      </div>
    </Dialog>
  );
}

function CollectDialog({ data, onClose, onDone }: { data: VisitDetail; onClose: () => void; onDone: () => void }) {
  const [invoice, setInvoice] = useState('');
  const owed = invoice ? (data.open.find((i) => i.id === invoice)?.owed ?? 0) : data.balance.open;
  const [amount, setAmount] = useState(String((data.balance.overdue || data.balance.open) / 100));
  const { busy, run } = useSave();
  const a = parseMoney(amount);
  const ok = a !== null && a > 0 && a <= owed;
  const save = async () => {
    if (ok && (await run(() => action('distribution.collect', { visit: data.visit.id, amount: a, invoice: invoice || undefined }), t('dst.collected_ok')))) onDone();
  };
  return (
    <Dialog
      title={t('dst.collect')}
      onClose={onClose}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" icon="check" busy={busy} disabled={!ok} onClick={save}>
            {t('dst.collect_save')}
          </Button>
        </>
      }
    >
      <div className="form-stack">
        <Field label={t('dst.collect_for')}>
          <select className="input" value={invoice} onChange={(e) => setInvoice(e.target.value)}>
            <option value="">{t('dst.oldest_first', { amount: money(data.balance.open) })}</option>
            {data.open.map((i) => (
              <option key={i.id} value={i.id}>
                {t('dst.invoice_owed', { name: i.name, amount: money(i.owed) })}
              </option>
            ))}
          </select>
        </Field>
        <Field label={t('dst.amount')} hint={t('dst.cash_hint')} error={a !== null && a > owed ? t('dst.too_much', { owed: money(owed) }) : a === null ? t('prl.amount_bad') : null}>
          <input className="input tabular" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} data-autofocus />
        </Field>
      </div>
    </Dialog>
  );
}

/* ---------------------------------------------------------------- Every rep's day */

function RepsTab() {
  const [date, setDate] = useState(todayIso());
  const [, setParams] = useSearchParams();
  const { data } = useAction<{ date: string; today: string; reps: { rep: string; rep_name: string; planned: number; done: number; skipped: number; orders: number; ordered: number; collected: number; cash: number }[] }>('distribution.overview', { date });
  const d = data && data.date === date ? data : null;
  return (
    <div className="rep-grid">
      <div className="toolbar">
        <label className="vat-month">
          <span>{t('dst.date')}</span>
          <input className="input date-input" type="date" value={date} onChange={(e) => e.target.value && setDate(e.target.value)} />
        </label>
      </div>
      {!d ? (
        <Skeleton h={240} r="var(--radius-lg)" />
      ) : (
        <>
          <section className="rep-kpis" aria-label={t('dst.totals')}>
            <Kpi label={t('dst.k_visits')} value={`${d.reps.reduce((s, r) => s + r.done, 0)}/${d.reps.reduce((s, r) => s + r.planned, 0)}`} />
            <Kpi label={t('dst.k_orders')} value={money(d.reps.reduce((s, r) => s + r.ordered, 0))} hint={tn('dst.orders_n', d.reps.reduce((s, r) => s + r.orders, 0))} />
            <Kpi label={t('dst.k_collected')} value={money(d.reps.reduce((s, r) => s + r.collected, 0))} />
            <Kpi label={t('dst.k_cash_all')} value={money(d.reps.reduce((s, r) => s + r.cash, 0))} />
          </section>
          <section className="panel rep-wide" aria-labelledby="dst-reps">
            <div className="panel-head">
              <h2 id="dst-reps">{t('dst.reps_day', { date: day(d.date) })}</h2>
            </div>
            <Table
              head={[t('dst.rep'), t('dst.c_visits'), t('dst.c_orders'), t('dst.k_collected'), t('dst.k_cash')]}
              phoneHide={[3, 4]}
              empty={t('dst.no_reps')}
              rows={d.reps.map((r) => ({
                key: r.rep,
                onClick: () => setParams({ rep: r.rep }, { replace: true }),
                cells: [
                  <span className="strong">{r.rep_name}</span>,
                  <span className="tabular">
                    {r.done}/{r.planned}
                    {r.skipped ? <span className="muted"> · {tn('dst.skipped_n', r.skipped)}</span> : null}
                  </span>,
                  <span className="tabular">{money(r.ordered)}</span>,
                  <span className="tabular">{money(r.collected)}</span>,
                  <span className="tabular strong">{money(r.cash)}</span>,
                ],
              }))}
            />
          </section>
        </>
      )}
    </div>
  );
}

/* ---------------------------------------------------------------- Routes */

function RoutesTab() {
  const { can } = useSession();
  const { data, reload } = useAction<RouteRow[]>('distribution.routes', {});
  const [open, setOpen] = useState<RouteRow | 'new' | null>(null);
  const plan = can('distribution.routes');
  if (!data) return <Skeleton h={240} r="var(--radius-lg)" />;
  return (
    <div className="rep-grid">
      {plan ? (
        <div className="toolbar">
          <Button variant="primary" icon="plus" onClick={() => setOpen('new')}>
            {t('dst.new_route')}
          </Button>
        </div>
      ) : null}
      {!data.length ? (
        <div className="rep-wide">
          <EmptyState icon="transfer" title={t('dst.no_routes')} body={plan ? t('dst.no_routes_body') : t('dst.no_routes_rep')} />
        </div>
      ) : (
        <section className="panel rep-wide" aria-label={t('dst.tab_routes')}>
          <Table
            head={[t('dst.c_route'), t('dst.rep'), t('dst.c_days'), t('dst.c_stops')]}
            phoneHide={[1]}
            left={[1, 2]}
            rows={data.map((r) => ({
              key: r.id,
              onClick: plan ? () => setOpen(r) : undefined,
              cells: [
                <span className="vat-party">
                  <span className="strong">{r.name}</span>
                  {!r.active ? <span className="muted small">{t('dst.inactive')}</span> : r.note ? <span className="muted small">{r.note}</span> : null}
                </span>,
                r.rep_name,
                <span className="dst-days">{r.days.map(weekdayName).join(' · ') || '—'}</span>,
                <span className="tabular">{r.stops}</span>,
              ],
            }))}
          />
        </section>
      )}
      {open ? (
        <RouteDialog
          route={open === 'new' ? null : open}
          onClose={() => setOpen(null)}
          onDone={() => {
            setOpen(null);
            void reload();
          }}
        />
      ) : null}
    </div>
  );
}

function RouteDialog({ route, onClose, onDone }: { route: RouteRow | null; onClose: () => void; onDone: () => void }) {
  if (!route) return <RouteForm route={null} stops={[]} onClose={onClose} onDone={onDone} />;
  return <SavedRoute route={route} onClose={onClose} onDone={onDone} />;
}

function SavedRoute({ route, onClose, onDone }: { route: RouteRow; onClose: () => void; onDone: () => void }) {
  const { data } = useAction<{ stops: { customer: string }[] }>('distribution.route', { route: route.id });
  if (!data) {
    return (
      <Dialog title={route.name} onClose={onClose} size="lg">
        <Skeleton h={240} />
      </Dialog>
    );
  }
  return <RouteForm route={route} stops={data.stops.map((s) => s.customer)} onClose={onClose} onDone={onDone} />;
}

function RouteForm({ route, stops: initial, onClose, onDone }: { route: RouteRow | null; stops: string[]; onClose: () => void; onDone: () => void }) {
  const { data: reps } = useAction<Person[]>('distribution.reps', {});
  const { data: customers } = useAction<{ id: string; name: string; town: string | null; active: boolean }[]>('invoicing.customers', {});
  const [name, setName] = useState(route?.name ?? '');
  const [rep, setRep] = useState(route?.rep ?? '');
  const [days, setDays] = useState<number[]>(route?.days ?? [1, 2, 3, 4, 5]);
  const [active, setActive] = useState(route?.active ?? true);
  const [note, setNote] = useState(route?.note ?? '');
  const [list, setStops] = useState<string[]>(initial);
  const [adding, setAdding] = useState('');
  const { busy, run } = useSave();
  const nameOf = (id: string) => customers?.find((c) => c.id === id)?.name ?? '…';
  const townOf = (id: string) => customers?.find((c) => c.id === id)?.town ?? null;
  const who = rep || reps?.[0]?.id || '';
  const move = (i: number, by: number) => {
    const next = [...list];
    const [x] = next.splice(i, 1);
    next.splice(i + by, 0, x!);
    setStops(next);
  };
  const ok = name.trim() && who && (!active || days.length);
  const save = async () => {
    if (!ok) return;
    if (await run(() => action('distribution.save_route', { route: route?.id, name: name.trim(), rep: who, days, active, note: note.trim() || null, customers: list }), t('dst.route_saved'))) onDone();
  };
  return (
    <Dialog
      title={route ? route.name : t('dst.new_route')}
      onClose={onClose}
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" icon="check" busy={busy} disabled={!ok} onClick={save}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="form-stack">
        <div className="dn-fields">
          <Field label={t('dst.route_name')} required>
            <input className="input" value={name} maxLength={80} onChange={(e) => setName(e.target.value)} data-autofocus />
          </Field>
          <Field label={t('dst.rep')} required>
            <select className="input" value={who} onChange={(e) => setRep(e.target.value)}>
              {(reps ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </Field>
        </div>
        <fieldset className="dn-owner">
          <legend>{t('dst.days')}</legend>
          <div className="dst-daypick" role="group">
            {[1, 2, 3, 4, 5, 6, 7].map((d) => (
              <button key={d} type="button" aria-pressed={days.includes(d)} className={days.includes(d) ? 'active' : ''} onClick={() => setDays((x) => (x.includes(d) ? x.filter((y) => y !== d) : [...x, d].sort()))}>
                {weekdayName(d)}
              </button>
            ))}
          </div>
        </fieldset>
        <Field label={t('dst.note')}>
          <input className="input" value={note} maxLength={500} onChange={(e) => setNote(e.target.value)} />
        </Field>
        <label className="switch-row">
          <span>
            <span className="switch-title">{t('dst.active')}</span>
            <span className="muted">{t('dst.active_hint')}</span>
          </span>
          <Switch checked={active} onChange={setActive} label={t('dst.active')} />
        </label>
        <section className="panel" aria-labelledby="dst-stops">
          <div className="panel-head">
            <h2 id="dst-stops">{t('dst.stops')}</h2>
          </div>
          {!customers ? (
            <Skeleton h={120} />
          ) : (
            <>
              <ol className="dst-stops">
                {list.map((id, i) => (
                  <li key={id}>
                    <span className="dst-seq tabular">{i + 1}</span>
                    <span className="dst-main">
                      <span className="dst-name">{nameOf(id)}</span>
                      {townOf(id) ? <span className="muted small">{townOf(id)}</span> : null}
                    </span>
                    <span className="row-actions">
                      <IconButton icon="up" label={t('dst.up')} onClick={() => move(i, -1)} disabled={i === 0} />
                      <IconButton icon="down" label={t('dst.down')} onClick={() => move(i, 1)} disabled={i === list.length - 1} />
                      <IconButton icon="x" label={t('dst.remove')} onClick={() => setStops(list.filter((x) => x !== id))} />
                    </span>
                  </li>
                ))}
                {!list.length ? <li className="muted">{t('dst.no_stops')}</li> : null}
              </ol>
              <div className="dst-add">
                <select className="input" aria-label={t('dst.add_stop')} value={adding} onChange={(e) => setAdding(e.target.value)}>
                  <option value="">{t('dst.add_stop')}</option>
                  {(customers ?? [])
                    .filter((c) => c.active !== false && !list.includes(c.id))
                    .map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.town ? `${c.name} · ${c.town}` : c.name}
                      </option>
                    ))}
                </select>
                <Button
                  icon="plus"
                  disabled={!adding}
                  onClick={() => {
                    setStops([...list, adding]);
                    setAdding('');
                  }}
                >
                  {t('dst.add')}
                </Button>
              </div>
            </>
          )}
        </section>
      </div>
    </Dialog>
  );
}

/* ---------------------------------------------------------------- Cash */

function CashTab() {
  const { me } = useSession();
  const { data, reload } = useAction<{
    held: { rep: string; rep_name: string; held: number; count: number; since: string }[];
    handovers: { id: string; date: string; rep_name: string; expected: number; counted: number; difference: number; note: string | null; received_by_name: string }[];
  }>('distribution.cash', {});
  const [taking, setTaking] = useState<{ rep: string; rep_name: string; held: number } | null>(null);
  if (!data) return <Skeleton h={240} r="var(--radius-lg)" />;
  return (
    <div className="rep-grid">
      <section className="panel rep-wide" aria-labelledby="dst-held">
        <div className="panel-head">
          <h2 id="dst-held">{t('dst.cash_held')}</h2>
        </div>
        <Table
          head={[t('dst.rep'), t('dst.c_since'), t('dst.c_collections'), t('dst.k_cash'), '']}
          phoneHide={[1, 2]}
          empty={t('dst.no_cash')}
          rows={data.held.map((h) => ({
            key: h.rep,
            onClick: h.rep === me?.userId ? undefined : () => setTaking(h),
            cells: [
              <span className="strong">{h.rep_name}</span>,
              <span className="tabular">{day(h.since)}</span>,
              <span className="tabular">{h.count}</span>,
              <span className="tabular strong">{money(h.held)}</span>,
              h.rep === me?.userId ? (
                <span className="muted small">{t('dst.not_own')}</span>
              ) : (
                <Button variant="secondary" icon="cash" onClick={(e) => (e.stopPropagation(), setTaking(h))}>
                  {t('dst.take_cash')}
                </Button>
              ),
            ],
          }))}
        />
      </section>
      <section className="panel rep-wide" aria-labelledby="dst-handovers">
        <div className="panel-head">
          <h2 id="dst-handovers">{t('dst.handovers')}</h2>
        </div>
        <Table
          view="distribution.cash"
          head={[t('dst.c_date'), t('dst.rep'), t('dst.c_expected'), t('dst.c_counted'), t('dst.c_difference'), t('dst.c_received')]}
          phoneHide={[2, 5]}
          left={[1, 5]}
          empty={t('dst.no_handovers')}
          rows={data.handovers.map((h) => ({
            key: h.id,
            cells: [
              <span className="tabular">{day(h.date)}</span>,
              <span className="vat-party">
                {h.rep_name}
                {h.note ? <span className="muted small">{h.note}</span> : null}
              </span>,
              <span className="tabular">{money(h.expected)}</span>,
              <span className="tabular">{money(h.counted)}</span>,
              <span className={`tabular strong ${h.difference < 0 ? 'text-danger' : ''}`}>{h.difference ? money(h.difference, { sign: true }) : '0'}</span>,
              h.received_by_name,
            ],
          }))}
        />
      </section>
      {taking ? (
        <HandoverDialog
          held={taking}
          onClose={() => setTaking(null)}
          onDone={() => {
            setTaking(null);
            void reload();
          }}
        />
      ) : null}
    </div>
  );
}

function HandoverDialog({ held, onClose, onDone }: { held: { rep: string; rep_name: string; held: number }; onClose: () => void; onDone: () => void }) {
  const [counted, setCounted] = useState('');
  const [note, setNote] = useState('');
  const { busy, run } = useSave();
  const c = counted.trim() ? parseMoney(counted) : null;
  const diff = c === null ? 0 : c - held.held;
  const ok = c !== null && c >= 0 && (diff === 0 || note.trim().length > 0);
  const save = async () => {
    if (ok && (await run(() => action('distribution.handover', { rep: held.rep, counted: c, note: note.trim() || null }), t('dst.handed_over')))) onDone();
  };
  return (
    <Dialog
      title={t('dst.take_from', { name: held.rep_name })}
      onClose={onClose}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" icon="check" busy={busy} disabled={!ok} onClick={save}>
            {t('dst.confirm_count')}
          </Button>
        </>
      }
    >
      <div className="form-stack">
        <p>{t('dst.expected_body', { amount: money(held.held) })}</p>
        <Field label={t('dst.counted')} hint={t('dst.counted_hint')}>
          <input className="input tabular" inputMode="decimal" value={counted} placeholder={String(held.held / 100)} onChange={(e) => setCounted(e.target.value)} data-autofocus />
        </Field>
        {c !== null && diff !== 0 ? (
          <div className="callout callout-warn" role="status">
            <Icon name="alert" size={20} />
            <p>{diff < 0 ? t('dst.short', { amount: money(-diff) }) : t('dst.over', { amount: money(diff) })}</p>
          </div>
        ) : null}
        <Field label={t('dst.note')} required={diff !== 0}>
          <textarea className="input" rows={2} maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} />
        </Field>
      </div>
    </Dialog>
  );
}
